import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { BidTagComponent } from '@web/shared/components/bid-tag.component';
import { ConstructionSiteCodeTagComponent } from '@web/shared/components/construction-site-code-tag.component';
import { ContactCodeTagComponent } from '@web/shared/components/contact-code-tag.component';
import { DutyCodeTagComponent } from '@web/shared/components/duty-code-tag.component';
import { EmployeeCodeTagComponent } from '@web/shared/components/employee-code-tag.component';
import { ExternalPriceListCodeTagComponent } from '@web/shared/components/external-price-list-code-tag.component';
import { LeadCodeTagComponent } from '@web/shared/components/lead-code-tag.component';
import { MetricComputationTagComponent } from '@web/shared/components/metric-computation-code-tag.component';
import { OwnerCompanyCodeTagComponent } from '@web/shared/components/owner-company-code-tag.component';
import { PriceListCodeTagComponent } from '@web/shared/components/price-list-code-tag.component';
import { ProductCodeTagComponent } from '@web/shared/components/product-code-tag.component';
import { ProjectCodeTagComponent } from '@web/shared/components/project-code-tag.component';
import { QuotationTagComponent } from '@web/shared/components/quotation-tag.component';
import { RealEstateCodeTagComponent } from '@web/shared/components/real-estate-code-tag.component';
import { ResourceCodeTagComponent } from '@web/shared/components/resource-code-tag.component';
import { ScoutingCodeTagComponent } from '@web/shared/components/scouting-code-tag.component';
import { SupplierCodeTagComponent } from '@web/shared/components/supplier-code-tag.component';
import { SvgIconComponent } from '@web/shared/components/svg-icon/svg-icon.component';
import { navBackMenuVisual } from './nav-back-menu-visual';
import { NavBackStackEntry } from './nav-back-stack.types';

@Component({
  selector: 'app-nav-back-menu-label',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LeadCodeTagComponent,
    ContactCodeTagComponent,
    RealEstateCodeTagComponent,
    BidTagComponent,
    QuotationTagComponent,
    MetricComputationTagComponent,
    ProjectCodeTagComponent,
    ConstructionSiteCodeTagComponent,
    SupplierCodeTagComponent,
    EmployeeCodeTagComponent,
    ScoutingCodeTagComponent,
    OwnerCompanyCodeTagComponent,
    PriceListCodeTagComponent,
    ProductCodeTagComponent,
    DutyCodeTagComponent,
    ExternalPriceListCodeTagComponent,
    ResourceCodeTagComponent,
    SvgIconComponent,
  ],
  host: {
    class: 'block',
  },
  styles: `
    /* Keep every code-tag chip the same footprint in the back menu. */
    :host ::ng-deep .p-tag {
      height: 1.25rem; /* h-5 */
      width: 5rem; /* w-20 */
      flex-shrink: 0;
      box-sizing: border-box;
    }
  `,
  template: `
    @let _entry = entry();
    @if (_entry.kind === 'entity' && _entry.codeTag) {
      <div class="flex items-center gap-2 min-w-0">
        <div class="shrink-0 leading-none">
          @switch (_entry.featureKey) {
            @case ('leads') {
              <app-lead-code-tag
                [lead]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('contacts') {
              <app-contact-code-tag
                [contact]="{ id: _entry.entityId, code: _entry.codeTag, kind: contactKind(_entry.entityKind) }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('real-estates') {
              <app-real-estate-code-tag
                [realEstate]="{ id: _entry.entityId, code: _entry.codeTag, kind: realEstateKind(_entry.entityKind) }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('biddings-bids') {
              <app-bid-tag [bid]="{ id: _entry.entityId, code: _entry.codeTag }" [clickable]="false" size="small" />
            }
            @case ('biddings-quotations') {
              <app-quotation-tag
                [quotation]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('biddings-metric-computations') {
              <app-metric-computation-tag
                [metricComputation]="{ id: _entry.entityId, code: _entry.codeTag, bid: { id: '' } }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('projects') {
              <app-project-code-tag
                [project]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('construction-sites') {
              <app-construction-site-code-tag
                [constructionSite]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('suppliers') {
              <app-supplier-code-tag
                [supplier]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('employees') {
              <app-employee-code-tag
                [employee]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('scoutings') {
              <app-scouting-code-tag
                [scouting]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('owner-company') {
              <app-owner-company-code-tag [clickable]="false" size="small" />
            }
            @case ('sales-price-lists') {
              <app-price-list-code-tag
                [priceList]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('sales-pricing-catalogs') {
              <app-external-price-list-code-tag
                [externalPriceList]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('sales-resources') {
              <app-resource-code-tag
                [resource]="{ id: _entry.entityId, code: _entry.codeTag, kind: resourceKind(_entry.entityKind) }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('sales-products') {
              <app-product-code-tag
                [product]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @case ('sales-duties') {
              <app-duty-code-tag
                [duty]="{ id: _entry.entityId, code: _entry.codeTag }"
                [clickable]="false"
                size="small"
              />
            }
            @default {
              <span class="inline-flex h-5 w-20 items-center justify-center font-bold !text-2xs">{{
                _entry.codeTag
              }}</span>
            }
          }
        </div>
        @if (_entry.displayName) {
          <span class="truncate text-sm">{{ _entry.displayName }}</span>
        }
      </div>
    } @else {
      @let _visual = visual();
      <div class="flex items-center gap-2.5 min-w-0">
        <span
          class="inline-flex size-7 shrink-0 items-center justify-center rounded-md"
          [style.background]="badgeBackground(_visual.color)"
          [style.color]="'var(' + _visual.color + ')'"
        >
          @if (_visual.svg) {
            <app-svg-icon [icon]="_visual.svg" class="size-3.5" />
          } @else {
            <i [class]="'text-sm leading-none ' + _visual.primeIcon"></i>
          }
        </span>
        <span class="truncate text-sm font-medium">{{ _entry.label }}</span>
      </div>
    }
  `,
})
export class NavBackMenuLabelComponent {
  readonly entry = input.required<NavBackStackEntry>();
  readonly visual = computed(() => navBackMenuVisual(this.entry()));

  badgeBackground(colorVariable: string): string {
    return `color-mix(in srgb, var(${colorVariable}) 16%, white)`;
  }

  contactKind(kind: string | undefined): 'naturalPerson' | 'legalPerson' | undefined {
    if (kind === 'naturalPerson' || kind === 'legalPerson') {
      return kind;
    }
    return undefined;
  }

  realEstateKind(kind: string | undefined): 'unit' | 'group' | 'complex' | undefined {
    if (kind === 'unit' || kind === 'group' || kind === 'complex') {
      return kind;
    }
    return undefined;
  }

  resourceKind(kind: string | undefined): 'material' | 'equipment' | 'labor' | 'markup' | 'adjustment' {
    switch (kind) {
      case 'material':
      case 'materials':
        return 'material';
      case 'equipment':
      case 'equipments':
        return 'equipment';
      case 'labor':
      case 'labors':
        return 'labor';
      case 'markup':
      case 'markups':
        return 'markup';
      case 'adjustment':
      case 'adjustments':
        return 'adjustment';
      default:
        return 'material';
    }
  }
}
